import React, { useState } from 'react';

import i18n from 'i18n-js';
import PropTypes from 'prop-types';
import { Box, Grid, Paper, IconButton, Typography, Divider, Button } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/AddCircle';
import { DnDtable } from 'components/UI';
import useStyles from './Rulesets.styled';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import EditIcon from '@material-ui/icons/Edit';
import { RulesModal } from 'components/Modals';

const Rulesets = ({ rules = [], onUpdate = () => {}, onDelete, action }) => {
  const classes = useStyles();
  const [openRulesModal, setOpenRulesModal] = useState(false);
  const [index, setIndex] = useState('');

  /**
   * Handle open/close rules modal
   */
  const handleOpenRulesModal = () => {
    setOpenRulesModal(!openRulesModal);
  };

  /**
   * Handla delete ruleset
   * @param {object} row Ruleset data
   */
  const handleDelete = (index) => {
    onDelete(index);
  };

  const handleUpdate = (data) => {
    onUpdate(data);
  };

  /**
   * Table columns
   * Show or hide columns with omit key
   */
  const columns = [
    {
      name: i18n.t('common.order'),
      selector: 'view_order',
      style: { width: '5%' },
      align: 'center',
      cell: (row) => row?.view_order || '--',
    },
    {
      name: i18n.t('ruleset.sku_type'),
      selector: 'invoice_item_type.pricing_type.external_name',
      style: { width: '25%' },
      cell: (row) => row?.invoice_item_type?.pricing_type?.external_name || '--',
    },
    {
      name: i18n.t('ruleset.sku_name'),
      selector: 'invoice_item_type.name',
      style: { width: '25%' },
      cell: (row) => {
        return (
          <>
            <Box>{row?.invoice_item_type?.name || '--'}</Box>
            <Box className={classes.skuDescription}>
              {row?.invoice_item_type?.pricing_type?.description || ''}
            </Box>
          </>
        );
      },
    },
    {
      name: i18n.t('ruleset.rate'),
      selector: 'invoice_item_type.unit_price',
      style: { width: '15%' },
      cell: (row) => {
        return row.invoice_item_type.pricing_type.name === 'SUBSCRIPTIONPERIODTIERED' ||
          row.invoice_item_type.pricing_type.name === 'SUBSCRIPTIONDEPOSITTIERED'
          ? i18n.t('ruleset.multitier')
          : typeof row?.unit_price === 'number'
            ? `$${row?.unit_price / 100}
          ${
            row.invoice_item_type.pricing_type.name === 'DISTANCEDRIVEN' &&
            row?.extra_arguments?.distance_allowance > 0
              ? i18n.t(`ruleset.per_extra_unit`, { unit: row?.invoice_item_type?.units?.one })
              : row?.invoice_item_type?.units?.per_unit ||
                i18n.t(`ruleset.per_unit`, { unit: row?.invoice_item_type?.units?.one }) ||
                ''
          }`
            : '--';
      },
    },
    {
      name: i18n.t('common.category'),
      selector: 'category',
      style: { width: '15%' },
      cell: (row) =>
        row?.invoice_item_type?.is_revenue
          ? i18n.t('skus.category_fee')
          : row?.invoice_item_type?.is_hold
            ? i18n.t('skus.category_hold')
            : i18n.t('skus.category_credit'),
    },
    {
      name: i18n.t('ruleset.times'),
      selector: 'times',
      style: { width: '25%' },
      cell: (row) => {
        if (row?.start === 1 && row?.end === 8) {
          return i18n.t('common.all_time');
        } else {
          const start = Math.abs(row?.start);
          const end = Math.abs(row?.end);
          const decimalstart = Math.round((start - Math.trunc(start)) * 24);
          const decimalend = Math.round((end - Math.trunc(end)) * 24);
          const startTime = moment()
            .set('isoWeekday', Math.trunc(start))
            .set('hour', decimalstart)
            .format('ddd ha');
          const endTime = moment()
            .set('isoWeekday', Math.trunc(end))
            .set('hour', decimalend)
            .format('ddd ha');
          return `${startTime} - ${endTime}`;
        }
      },
    },
  ];

  if (action !== 'details') {
    columns.push({
      name: i18n.t('common.actions'),
      selector: 'action',
      style: { width: '5%' },
      cell: (row, index) => {
        return (
          <Box display="flex" justifyContent="center">
            <IconButton
              aria-label="edit"
              size="medium"
              onClick={() => {
                setIndex(index);
                handleOpenRulesModal();
              }}
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
            <IconButton aria-label="delete" size="medium" onClick={() => handleDelete(index)}>
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Box>
        );
      },
    });
  }

  return (
    <Grid item xs={12}>
      <Grid item>
        <Typography variant="h6" align="left">
          <Box fontWeight="fontWeightBold" fontSize={13} mb={1} style={{ height: '100%' }}>
            {i18n.t(`common.rules`)}
          </Box>
        </Typography>
      </Grid>
      <Paper elevation={1} className={classes.paper}>
        <DnDtable
          data={rules}
          columns={columns}
          editable={action === 'details' ? false : true}
          onUpdate={handleUpdate}
        />
        {action !== 'details' && (
          <>
            <Divider />
            <Grid item xs={12}>
              <Grid
                container
                spacing={1}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Grid item>
                  <Box display="flex" justifyContent="center" fontSize={13}>
                    <Button
                      color="primary"
                      fontSize={13}
                      style={{
                        textTransform: 'capitalize',
                        marginRight: 15,
                      }}
                      onClick={() => {
                        setIndex('');
                        handleOpenRulesModal();
                      }}
                      startIcon={<AddIcon />}
                    >
                      {i18n.t('ruleset.add_rule')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
      {openRulesModal && (
        <RulesModal index={index} open={openRulesModal} handleClick={handleOpenRulesModal} />
      )}
    </Grid>
  );
};

Rulesets.propTypes = {
  rules: PropTypes.arrayOf(PropTypes.object),
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  action: PropTypes.any,
};

export default withRouter(Rulesets);
